import { format } from 'date-fns';
import { Form, Formik } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';

import { GeneralSettingsSection } from './GeneralSettingsSection';
import { RecurringSettingsSection } from './RecurringSettingsSection';
import { ResourceSettingsSection } from './ResourceSettingsSection';
import { StaffSettingsSection } from './StaffSettingsSection';
import { useEditEventDialog } from './useEditEventDialog';

import type { TFunction } from 'i18next';
import type { Appointment, Conflict } from 'src/models/appointment/_types';
import type { Identifier } from 'src/types/utilityTypes';

/**
 * Format a single conflict for display
 * Matches v2 showConflictDialog() lines 219-243
 */
const formatConflict = (
  conflict: Conflict,
  t: TFunction<'translation', 'calendar.editEventDialog'>
): React.ReactNode => {
  const date = format(new Date(conflict.startTime * 1000), 'MMM d, yyyy');
  const startTime = format(new Date(conflict.startTime * 1000), 'h:mm a');
  const endTime = format(new Date(conflict.endTime * 1000), 'h:mm a');

  let details = '';
  if (conflict.conflictType === 'resource') {
    details = `[${t('asset')}] ${conflict.resourceName}`;
    if (conflict.serviceName) details += ` - ${conflict.serviceName}`;
  } else {
    details = `${conflict.serviceName || t('session')} ${t('with')} ${conflict.trainerNames}`;
  }
  if (conflict.locationName) details += ` ${t('at')} ${conflict.locationName}`;

  return (
    <li key={`${conflict.startTime}-${conflict.conflictType}`}>
      <strong>{date}</strong> {startTime} - {endTime}
      <br />
      <em>{details}</em>
    </li>
  );
};

interface EditEventDialogProps {
  open: boolean;
  onClose: () => void;
  appointment?: Appointment | null;
  initialDate?: Date | null;
  initialServiceId?: Identifier | null;
  initialTrainerId?: Identifier | null;
  onSaveSuccess?: (appointment: Appointment) => void;
}

export const EditEventDialog: FC<EditEventDialogProps> = ({
  open,
  onClose,
  appointment,
  initialDate,
  initialServiceId,
  initialTrainerId,
  onSaveSuccess,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'calendar.editEventDialog' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const {
    formikConfig,
    getServiceById,
    isSubmitting,
    isEditing,
    isRecurring,
    // Conflict handling
    conflictDialogOpen,
    conflictData,
    handleOverrideConflicts,
    handleCancelConflict,
    // Recurring prompts
    scopeDialogOpen,
    notifyDialogOpen,
    handleScopeSelect,
    handleNotifySelect,
    handleCancelRecurringPrompts,
  } = useEditEventDialog({
    appointment,
    initialDate,
    initialServiceId,
    initialTrainerId,
    onClose,
    onSaveSuccess,
  });

  const title = isEditing ? t('editSession') : t('newSession');

  return (
    <>
      <Formik {...formikConfig}>
        {({ submitForm, resetForm }) => (
          <Dialog
            open={open}
            onClose={() => {
              resetForm();
              onClose();
            }}
            title={title}
            maxWidth="md"
            fullWidth
            actions={[
              {
                label: tCommon('actions.save'),
                onClick: submitForm,
                color: 'primary',
                variant: 'contained',
                loading: isSubmitting,
              },
            ]}
          >
            <Form>
              <Stack spacing={4} sx={{ py: 1 }}>
                <GeneralSettingsSection
                  getServiceById={getServiceById}
                  isEditing={isEditing}
                />

                <StaffSettingsSection />

                <ResourceSettingsSection getServiceById={getServiceById} />

                <RecurringSettingsSection isRecurring={isRecurring} />
              </Stack>
            </Form>
          </Dialog>
        )}
      </Formik>

      {/* Scope Dialog - v2 lines 1655-1678: "Only this session" vs "All future sessions" */}
      <ConfirmDialog
        open={scopeDialogOpen}
        onClose={handleCancelRecurringPrompts}
        title={t('updateRecurringSession')}
        actions={[
          {
            label: t('onlyThisSession'),
            onClick: () => handleScopeSelect('this'),
            variant: 'outlined',
          },
          {
            label: t('allFutureSessions'),
            onClick: () => handleScopeSelect('all'),
            variant: 'contained',
            color: 'primary',
          },
        ]}
      >
        {t('updateRecurringMessage')}
      </ConfirmDialog>

      {/* Notify Dialog - v2 lines 1688-1716: "Notify attendees?" */}
      <ConfirmDialog
        open={notifyDialogOpen}
        onClose={handleCancelRecurringPrompts}
        title={t('notifyAttendees')}
        actions={[
          {
            label: tCommon('no'),
            onClick: () => handleNotifySelect(false),
            variant: 'outlined',
          },
          {
            label: tCommon('yes'),
            onClick: () => handleNotifySelect(true),
            variant: 'contained',
            color: 'primary',
          },
        ]}
      >
        {t('notifyAttendeesMessage')}
      </ConfirmDialog>

      {/* Conflict Dialog - v2 showConflictDialog() lines 208-276 */}
      <ConfirmDialog
        open={conflictDialogOpen}
        onClose={handleCancelConflict}
        title={t('schedulingConflict')}
        actions={[
          {
            label: tCommon('actions.cancel'),
            onClick: handleCancelConflict,
            variant: 'outlined',
          },
          {
            label: t('overrideConflict'),
            onClick: handleOverrideConflicts,
            variant: 'contained',
            color: 'warning',
          },
        ]}
      >
        <Box sx={{ textAlign: 'left', maxHeight: 300, overflowY: 'auto' }}>
          <Typography variant="body1" gutterBottom>
            {t('foundConflicts', { count: conflictData?.totalConflicts || 0 })}
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {conflictData?.conflicts.map((conflict) =>
              formatConflict(conflict, t)
            )}
            {conflictData &&
              conflictData.totalConflicts > conflictData.conflicts.length && (
                <li>
                  <em>
                    {t('andMoreConflicts', {
                      count:
                        conflictData.totalConflicts -
                        conflictData.conflicts.length,
                    })}
                  </em>
                </li>
              )}
          </Box>
        </Box>
        <Typography variant="body2" sx={{ mt: 2 }}>
          {t('overrideAndCreateMessage')}
        </Typography>
      </ConfirmDialog>
    </>
  );
};
